import { Briefcase, GraduationCap, Users, Star, TrendingUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Experience = ({ experience, education }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'internship':
        return Briefcase;
      case 'leadership':
        return Users;
      default:
        return Briefcase;
    }
  };

  const getIconColor = (type) => {
    return type === 'leadership' ? '#9A7B4F' : '#C4A76C';
  };

  // Separate featured experience
  const featuredExp = experience.find(exp => exp.featured);
  const otherExp = experience.filter(exp => !exp.featured);

  return (
    <section id="experience" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0D1117] mb-4">
            My Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Education, professional experience, and leadership roles shaping my career
          </p>
        </div>

        {/* Featured Latest Internship */}
        {featuredExp && (
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6">
              <Star className="text-[#9A7B4F]" size={24} fill="#9A7B4F" />
              <h3 className="text-2xl font-bold text-[#0D1117]">Latest Experience</h3>
            </div>
            <Card className="border-2 border-[#9A7B4F] shadow-2xl hover:shadow-3xl transition-all bg-gradient-to-br from-[#9A7B4F]/5 to-white">
              <CardContent className="p-8">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-[#9A7B4F] to-[#C4A76C] flex items-center justify-center shadow-lg">
                    <Briefcase size={32} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h4 className="text-2xl font-bold text-[#0D1117] mb-2">{featuredExp.title}</h4>
                        <p className="text-[#9A7B4F] font-semibold text-lg">{featuredExp.organization}</p>
                        {featuredExp.location && (
                          <p className="text-sm text-gray-600">{featuredExp.location}</p>
                        )}
                      </div>
                      <Badge className="bg-[#9A7B4F] text-white hover:bg-[#C4A76C] text-sm px-4 py-2">
                        {featuredExp.duration}
                      </Badge>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">{featuredExp.description}</p>
                    
                    {featuredExp.achievements && featuredExp.achievements.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="font-semibold text-[#0D1117] flex items-center gap-2">
                          <TrendingUp size={18} className="text-[#9A7B4F]" />
                          Key Achievements:
                        </p>
                        <ul className="space-y-2 ml-6">
                          {featuredExp.achievements.map((achievement, idx) => (
                            <li key={idx} className="text-gray-700 flex items-start gap-2">
                              <span className="text-[#9A7B4F] mt-1">•</span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Education Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-[#0D1117] mb-8 flex items-center gap-3">
            <GraduationCap className="text-[#9A7B4F]" size={28} />
            Education
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {education.map((edu) => (
              <Card key={edu.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="mb-3">
                    <h4 className="text-xl font-bold text-[#0D1117] mb-2">{edu.degree}</h4>
                    <p className="text-[#9A7B4F] font-semibold">{edu.institution}</p>
                    <p className="text-sm text-gray-600">{edu.location}</p>
                  </div>
                  <Badge className="bg-[#0D1117]/10 text-[#0D1117] hover:bg-[#0D1117]/20 mb-3">
                    {edu.duration}
                  </Badge>
                  <p className="text-gray-700 mt-3">{edu.description}</p>
                  {edu.subjects && (
                    <p className="text-sm text-gray-600 mt-3">
                      <strong>Subjects:</strong> {edu.subjects}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Other Experience Timeline */}
        <div>
          <h3 className="text-2xl font-bold text-[#0D1117] mb-8 flex items-center gap-3">
            <Briefcase className="text-[#9A7B4F]" size={28} />
            Professional Experience & Leadership
          </h3>
          <div className="space-y-6">
            {otherExp.map((exp) => {
              const Icon = getIcon(exp.type);
              const iconColor = getIconColor(exp.type);
              return (
                <Card key={exp.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center">
                        <Icon size={24} style={{ color: iconColor }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div>
                            <h4 className="text-xl font-bold text-[#0D1117]">{exp.title}</h4>
                            <p className="text-[#9A7B4F] font-semibold">{exp.organization}</p>
                          </div>
                          <Badge className="bg-[#0D1117]/10 text-[#0D1117] hover:bg-[#0D1117]/20">
                            {exp.duration}
                          </Badge>
                        </div>
                        <p className="text-gray-700 mt-2 leading-relaxed">{exp.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
